import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ChatShortcut } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useShortcutMutations } from '../hooks/use-shortcut-mutations';
import { useShortcuts } from '../hooks/use-shortcuts';
import type { ShortcutScope } from '../schemas/shortcut-schema';

import { ConfirmDialog } from './confirm-dialog';
import { ShortcutDialog } from './shortcut-dialog';

interface EditorState {
  shortcut: ChatShortcut | null;
  scope: ShortcutScope;
  shared: boolean;
}

function ShortcutList({
  items,
  canEdit,
  onEdit,
  onDelete,
  emptyText,
  label,
}: {
  items: ChatShortcut[];
  canEdit: boolean;
  onEdit: (shortcut: ChatShortcut) => void;
  onDelete: (shortcut: ChatShortcut) => void;
  emptyText: string;
  label: string;
}) {
  const { t } = useTranslation('chatSettings');
  if (items.length === 0) return <p className="text-muted-foreground text-sm">{emptyText}</p>;
  return (
    <ul className="divide-y rounded-md border" aria-label={label}>
      {items.map((shortcut) => (
        <li key={shortcut.id} className="flex items-start gap-3 p-3">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-medium">/{shortcut.key}</span>
              <span className="text-sm">{shortcut.title}</span>
              {shortcut.scope === 'personal' ? null : (
                <Badge variant="outline">{t(`shortcuts.scopes.${shortcut.scope}`)}</Badge>
              )}
              {shortcut.attachments.length > 0 ? (
                <Badge variant="secondary">
                  {t('shortcuts.attachmentCount', { count: shortcut.attachments.length })}
                </Badge>
              ) : null}
              {shortcut.productIds.length > 0 ? (
                <Badge variant="secondary">
                  {t('shortcuts.productCount', { count: shortcut.productIds.length })}
                </Badge>
              ) : null}
            </p>
            <p className="text-muted-foreground line-clamp-2 text-sm whitespace-pre-line">
              {shortcut.body}
            </p>
          </div>
          {canEdit ? (
            <div className="flex shrink-0 gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('shortcuts.edit', { key: shortcut.key })}
                onClick={() => onEdit(shortcut)}
              >
                <Pencil aria-hidden />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('shortcuts.delete', { key: shortcut.key })}
                onClick={() => onDelete(shortcut)}
              >
                <Trash2 aria-hidden />
              </Button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Canned replies typed as `/key` in the inbox composer. Shared ones (store or organisation)
 * need `conversations.manage`; anyone who replies (`conversations.write`) keeps their own.
 */
export function ShortcutsTab({ storeId }: { storeId: string }) {
  const { t } = useTranslation('chatSettings');
  const { can } = usePermissions();
  const canShare = can('conversations.manage');
  const canOwn = can('conversations.write');
  const query = useShortcuts(storeId);
  const { remove } = useShortcutMutations(storeId);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [deleting, setDeleting] = useState<ChatShortcut | null>(null);

  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data) {
    return (
      <div className="flex max-w-3xl flex-col gap-3" aria-busy="true">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }
  const shared = query.data.filter((s) => s.scope !== 'personal');
  const mine = query.data.filter((s) => s.scope === 'personal');

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <FormSection
        title={t('shortcuts.sharedTitle')}
        description={t('shortcuts.sharedDescription')}
      >
        <ShortcutList
          items={shared}
          canEdit={canShare}
          label={t('shortcuts.sharedTitle')}
          emptyText={t('shortcuts.sharedEmpty')}
          onEdit={(shortcut) => setEditor({ shortcut, scope: shortcut.scope, shared: true })}
          onDelete={setDeleting}
        />
        {canShare ? (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditor({ shortcut: null, scope: 'store', shared: true })}
            >
              <Plus aria-hidden />
              {t('shortcuts.addShared')}
            </Button>
          </div>
        ) : null}
      </FormSection>
      <FormSection title={t('shortcuts.mineTitle')} description={t('shortcuts.mineDescription')}>
        <ShortcutList
          items={mine}
          canEdit={canOwn}
          label={t('shortcuts.mineTitle')}
          emptyText={t('shortcuts.mineEmpty')}
          onEdit={(shortcut) => setEditor({ shortcut, scope: 'personal', shared: false })}
          onDelete={setDeleting}
        />
        {canOwn ? (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditor({ shortcut: null, scope: 'personal', shared: false })}
            >
              <Plus aria-hidden />
              {t('shortcuts.addMine')}
            </Button>
          </div>
        ) : null}
      </FormSection>
      {editor ? (
        <ShortcutDialog
          key={editor.shortcut?.id ?? `new-${editor.scope}`}
          storeId={storeId}
          open
          onOpenChange={(open) => {
            if (!open) setEditor(null);
          }}
          shortcut={editor.shortcut}
          scope={editor.scope}
          shared={editor.shared}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={t('shortcuts.deleteTitle', { key: deleting?.key ?? '' })}
        description={t('shortcuts.deleteDescription')}
        confirmLabel={t('shortcuts.deleteConfirm')}
        pending={remove.isPending}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id, { onSettled: () => setDeleting(null) });
        }}
      />
    </div>
  );
}
